import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

export const useIBLink = (
  defaultReferralCode?: string,
  multiTierReferralCode?: string
): {
  ibLink?: string
  mtLink?: string
  isLoading: boolean
  fetchShortenedIBLink: (
    refCode: string,
    shortenUrl?: boolean
  ) => Promise<{ shortUrl: string; url: string }>
  refreshIBLinks: () => void
} => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const locale = useSessionLanguage()
  const domain = window.location.host
  const queryKey = queryKeys.ibLink(defaultReferralCode, multiTierReferralCode, locale)

  const fetchShortenedIBLink = useCallback(
    async (referralCode: string, shortenUrl = false) => {
      return apiClient.getReferralLink(locale, domain, shortenUrl, referralCode)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [locale, domain, apiClient]
  )

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      apiClient.getReferralLink(
        locale,
        domain,
        false,
        defaultReferralCode ?? multiTierReferralCode
      ),
    enabled: !!(defaultReferralCode || multiTierReferralCode),
  })

  const { url: urlLink, domainUrl, landingPageUrl } = data || {}
  const newLp = `${domainUrl}${landingPageUrl}`

  const ibUrl = urlLink?.replace(
    /(lp=).*(&|$)/,
    (match, p1, p2) => `${p1}${encodeURIComponent(newLp)}${p2}`
  )

  let ibMultiTierUrl: string | undefined
  if (defaultReferralCode && multiTierReferralCode) {
    const url = ibUrl ? new URL(ibUrl) : null
    url && multiTierReferralCode && url.searchParams.set('utm_content', multiTierReferralCode)
    ibMultiTierUrl = url?.toString()
  }

  return {
    ibLink: defaultReferralCode && ibUrl,
    mtLink: !defaultReferralCode ? ibUrl : ibMultiTierUrl,
    isLoading,
    fetchShortenedIBLink,
    refreshIBLinks: () => queryClient.invalidateQueries({ queryKey }),
  }
}
